import React, { Fragment } from 'react';
import { Line as KonvaLine, Arrow, Rect } from 'react-konva';
import { observer } from 'mobx-react-lite';

const HANDLE_SIZE = 10;
const DEG = Math.PI / 180;

const LineElementComponent = observer(({ element, isSelected, onSelect, onTransformEnd, onDragEnd, groupChild }) => {
  if (!element.visible) return null;

  const rot = (element.rotation || 0) * DEG;
  const cosR = Math.cos(rot);
  const sinR = Math.sin(rot);

  // Start and end points in canvas coordinates
  const startX = element.x;
  const startY = element.y;
  const endX = element.x + element.width * cosR;
  const endY = element.y + element.width * sinR;

  const hasArrow = element.endHead === 'arrow' || element.startHead === 'arrow';

  // Drag the whole line
  const handleLineDragEnd = (e) => {
    const dx = e.target.x();
    const dy = e.target.y();
    // The line is rendered at (0,0) with absolute points, so dx/dy is the drag offset
    e.target.x(0);
    e.target.y(0);
    element.set({
      x: element.x + dx,
      y: element.y + dy,
    });
    if (onDragEnd) onDragEnd(e);
  };

  // Drag the START handle
  const handleStartDrag = (e) => {
    const newSX = e.target.x() + HANDLE_SIZE / 2;
    const newSY = e.target.y() + HANDLE_SIZE / 2;
    const dx = endX - newSX;
    const dy = endY - newSY;
    const newWidth = Math.max(5, Math.sqrt(dx * dx + dy * dy));
    const newRotation = Math.atan2(dy, dx) / DEG;

    element.set({
      x: newSX,
      y: newSY,
      width: newWidth,
      rotation: newRotation,
    });

    // Reset handle position since the line re-renders from new model coords
    e.target.x(newSX - HANDLE_SIZE / 2);
    e.target.y(newSY - HANDLE_SIZE / 2);
  };

  const handleStartDragEnd = () => {
    if (onDragEnd) onDragEnd();
  };

  // Drag the END handle
  const handleEndDrag = (e) => {
    const newEX = e.target.x() + HANDLE_SIZE / 2;
    const newEY = e.target.y() + HANDLE_SIZE / 2;
    const dx = newEX - startX;
    const dy = newEY - startY;
    const newWidth = Math.max(5, Math.sqrt(dx * dx + dy * dy));
    const newRotation = Math.atan2(dy, dx) / DEG;

    element.set({
      width: newWidth,
      rotation: newRotation,
    });

    // Reset handle position
    e.target.x(newEX - HANDLE_SIZE / 2);
    e.target.y(newEY - HANDLE_SIZE / 2);
  };

  const handleEndDragEnd = () => {
    if (onDragEnd) onDragEnd();
  };

  const lineNode = hasArrow ? (
    <Arrow
      id={element.id}
      name={element.name || 'line'}
      points={[startX, startY, endX, endY]}
      stroke={element.color || '#000000'}
      strokeWidth={2}
      fill={element.color || '#000000'}
      pointerLength={10}
      pointerWidth={10}
      dash={element.dash?.length > 0 ? [...element.dash] : undefined}
      hitStrokeWidth={20}
      opacity={element.opacity}
      draggable={!groupChild && element.draggable}
      visible={element.visible}
      shadowEnabled={element.shadowEnabled}
      shadowColor={element.shadowColor}
      shadowBlur={element.shadowBlur}
      shadowOffsetX={element.shadowOffsetX}
      shadowOffsetY={element.shadowOffsetY}
      shadowOpacity={element.shadowOpacity}
      onDragEnd={handleLineDragEnd}
    />
  ) : (
    <KonvaLine
      id={element.id}
      name={element.name || 'line'}
      points={[startX, startY, endX, endY]}
      stroke={element.color || '#000000'}
      strokeWidth={2}
      dash={element.dash?.length > 0 ? [...element.dash] : undefined}
      hitStrokeWidth={20}
      opacity={element.opacity}
      draggable={!groupChild && element.draggable}
      visible={element.visible}
      shadowEnabled={element.shadowEnabled}
      shadowColor={element.shadowColor}
      shadowBlur={element.shadowBlur}
      shadowOffsetX={element.shadowOffsetX}
      shadowOffsetY={element.shadowOffsetY}
      shadowOpacity={element.shadowOpacity}
      onDragEnd={handleLineDragEnd}
    />
  );

  return (
    <Fragment>
      {lineNode}

      {/* Endpoint handles — only visible when selected */}
      {isSelected && (
        <Fragment>
          {/* Start handle */}
          <Rect
            x={startX - HANDLE_SIZE / 2}
            y={startY - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill="#ffffff"
            stroke="#0d83cd"
            strokeWidth={1.5}
            draggable
            onDragMove={handleStartDrag}
            onDragEnd={handleStartDragEnd}
          />
          {/* End handle */}
          <Rect
            x={endX - HANDLE_SIZE / 2}
            y={endY - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill="#ffffff"
            stroke="#0d83cd"
            strokeWidth={1.5}
            draggable
            onDragMove={handleEndDrag}
            onDragEnd={handleEndDragEnd}
          />
        </Fragment>
      )}
    </Fragment>
  );
});

LineElementComponent.displayName = 'LineElement';
export default LineElementComponent;
